import classNames                    from 'classnames';
import * as React                    from 'react';
import { useTranslation }            from 'react-i18next';
import { useHistory, useRouteMatch } from 'react-router-dom';

import { GalleryImage }          from '../../../../components/sliders/GallerySlider/components';
import RenderFilterTabs          from '../../../../components/tabs/RenderFilterTabs';
import { useCommon }             from '../../../../providers/common';
import { useInformation }        from '../../../../providers/information';
import type { TInformationType } from '../../../../providers/information';

import type { InformationSidebarProps } from './information.sidebar.props';

const InformationSidebar: React.FC<InformationSidebarProps> = function(props) {
  const history = useHistory();
  const match   = useRouteMatch<Record<'page' | 'urlKey', string>>();
  const { t }   = useTranslation();

  const {
          activeCategory,
          handleCategoryChange,
          scrollToArticle,
        } = props;

  const { pages }   = useCommon();
  const information = useInformation();

  const [isOpen, setIsOpen] = React.useState(true);

  const tabs = React.useMemo(() => {
    const items = [
      {
        label: t('client_informationSidebar_tab_terminology'),
        value: 'terminology',
      },
    ];

    if (!information.isInterior) {
      items.unshift({
        label: t('client_informationSidebar_tab_questions'),
        value: 'questions',
      });
    }

    return items;
  }, [information.isInterior]);

  const goTo         = React.useCallback((value: TInformationType) => {
    const page = (pages as any).find((page: any) => {
      return page.template === `${value}${information.isInterior ? '_interior' : ''}`;
    });

    if (page && information.template !== page.template) {
      scrollTo({
        left: 0,
        top:  0,
      });

      information.changeTab(page.template);
      history.replace(`/${page.urlKey}`);
    }
  }, [information, pages]);
  const handleChange = React.useCallback((url: string) => {
    if (window.screen.width <= 991 && !isOpen) {
      setIsOpen(true);
    }

    scrollToArticle(url);
  }, [isOpen, scrollToArticle]);
  const toggleState  = React.useCallback(() => {
    setIsOpen((open) => !open);
  }, []);

  return (
    <aside
      className={classNames({ open: isOpen })}
      id={'sidebar'}
    >
      <div>
        <div
          className={'btn-filter'}
          onClick={toggleState}
        >
          <span className={'ic-arrow-left'} />
          <span className={'title'}>{t('client_informationSidebar_title')}</span>
        </div>
        <section className={'top'}>
          <RenderFilterTabs
            action={goTo}
            active={information.type}
            options={tabs}
          />
        </section>
        <section className={'body'}>
          {information.items.map((category) => {
            if (information.type === 'questions') {
              return (
                <div
                  className={classNames('sidebar_item', {active: category.id === activeCategory})}
                  key={category.id}
                  onClick={() => {
                    handleCategoryChange(category.id);
                  }}
                >{category.label}</div>
              );
            }

            return (
              <div key={category.id}>
                <div className={'body'}>
                  {category.items.map((item) => {
                    return (
                      <div
                        className={classNames('sidebar_item', { active: match.params.urlKey === item.urlKey })}
                        key={item.id}
                        onClick={() => {
                          handleChange(item.urlKey);
                        }}
                      >{item.label}</div>
                    );
                  })}
                </div>
              </div>
            );
          })}
          {information.type === 'questions' && information.promotion && (
            <div
              className={classNames('banner_container', {
                link: !!information.promotion?.promotionLink,
              })}
              onClick={() => {
                if (information.promotion?.promotionLink) {
                  history.push(information.promotion.promotionLink);
                }
              }}
            >
              <GalleryImage
                src={information.promotion.promotionBanner}
                size={205}
                type={'image'}
              />
            </div>
          )}
        </section>
      </div>
    </aside>
  );
};

export default InformationSidebar;